/* eslint-disable max-lines */
import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Controller, useFormContext } from 'react-hook-form';
import { Adaptive, RegularButton as Button, TabList, Tab, InfoBar } from 'fronton-react';
import { InfoIcon } from '@fronton/icons-react';
import { Pencil } from 'assets/icons/pencil';
import { RouteContext, TInputParams } from 'entries/common/widgets/context';
import { gaFocusOnDimensions } from 'ga-events/ga-focus-dimensions';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { TRoomsParams } from 'common/types/floor-params.type';
import { getAreaState } from 'pages/common/helpers/get-area';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { TInputParams as TRoomsInputParams } from 'pages/common/types';
import { LayingTypeField } from 'pages/common/components/LayingTypeField';
import { activeRoomId, calculatedRoomsParams, defaultRoomParams, roomArea } from 'pages/common/store/rooms';
import { useIsNeedToResetActiveRoom } from 'pages/common/hooks/use-is-need-to-reset-active-room';
import { AdaptiveInput } from 'pages/common/components/AdaptiveInput';
import { tabListLabels } from 'pages/common/consts/dimensions-tab-list';
import { getClearedRoomParams } from 'pages/common/components/Rooms/helpers';
import { dimensionsErrors, dimensionsLabels } from '../../const';
import { ParametersEnum } from '../../types';
import { useFilteredInputParams } from '../hooks/use-filtered-input-params';
import {
  checkIsSumWrong,
  setIsAreaEnabledHandler,
  setShowWarningBarHandler,
  getRoomParametersAreaText,
  getRoomParamsQuantity,
  roomParametersValidation,
} from './helpers';
import { useFormConditions } from '../../hooks/use-dimension-form-conditions';
import { FrontendConfigurationFields } from './components/FrontendConfigurationFields';
import {
  RoomParametersFormWrapper,
  RoomParametersFormControls,
  ButtonContainer,
  HeaderContainer,
  InputsContainer,
  styledTabList,
  LayingTypeContainer,
  InputContainerColumn,
  InputContainerRow,
  infobar,
} from './styles';
import { FrontendConfigurationLayingTypes } from './components/FrontendConfigurationLaying';
import { FrontendConfigurationChackboxes } from './components/FrontendConfigurationCheckboxes';

interface IRoomParametersForm {
  hasNegativeParams: boolean | undefined;
  setHasNegativeParams: Dispatch<SetStateAction<boolean | undefined>>;
  setShowWarningBar: Dispatch<SetStateAction<boolean>>;
  filteredInputParams: TInputParams;
  setInputParams: Dispatch<SetStateAction<TInputParams>>;
  hasArea: boolean;
  activeTabIndex: number;
  setActiveTabIndex: Dispatch<SetStateAction<number>>;
}

export const RoomParametersForm = ({
  hasNegativeParams,
  setHasNegativeParams,
  setShowWarningBar,
  filteredInputParams,
  setInputParams,
  hasArea,
  activeTabIndex,
  setActiveTabIndex,
}: IRoomParametersForm) => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();

  const { mobile } = useBreakpoints() ?? {};

  const { setContext: setRoute } = useContext(RouteContext);
  const formValue = getValues();

  const { hasThickness } = useFormConditions();

  const [isAreaEnabled, setIsAreaEnabled] = useRecoilState(roomArea);
  const [calculatedRoomParams, setCalculatedRoomParams] = useRecoilState<TRoomsParams>(
    calculatedRoomsParams
  );
  const [selectedRoomId, setSelectedRoomId] = useRecoilState(activeRoomId);
  const { isCalculatedRoomsParamsHasOnlySquare } = getClearedRoomParams(calculatedRoomParams);

  const { defaultInputParams, frontendConfigurationInputs } = useFilteredInputParams();
  const { isNeedToResetActiveRoom } = useIsNeedToResetActiveRoom();
  const frontendConfigurationInputParams = new Set(frontendConfigurationInputs.FREE?.flatMap(x => x.name));
  const filteredInputParamsWithoutFrontendConfigurationInputParams = frontendConfigurationInputParams
    ? filteredInputParams.filter(param => !frontendConfigurationInputParams.has(param))
    : filteredInputParams;
  const isShowInfobar = selectedRoomId !== '' && !hasArea && isCalculatedRoomsParamsHasOnlySquare;

  const handleUnselectRoom = () => {
    setSelectedRoomId('');
  };

  const handleChange = (onChange: (value: string) => void) => (value: string) => {
    if (calculatedRoomParams.calculatorId?.length) {
      // Сброс значения и выбраной кнопки при изменении
      setCalculatedRoomParams(defaultRoomParams);
      setValue(ParametersEnum.width, '');
      setValue(ParametersEnum.length, '');
      setValue(ParametersEnum.area, '');
    }
    handleUnselectRoom();
    onChange(value);
  };

  const changeInputsParams = (isEnabled: boolean) => {
    const areaState = getAreaState({
      inputParams: defaultInputParams as TRoomsInputParams,
      isEnabled,
    });

    setInputParams(areaState);
  };

  const onAreaButtonClick = (value: string) => {
    const areaEnabledState = !isAreaEnabled;
    setIsAreaEnabled(areaEnabledState);
    setActiveTabIndex(Number(value));
    if (isNeedToResetActiveRoom) {
      handleUnselectRoom();
    }
    gaClickOnGoods({
      title: 'Параметры помещения',
      name: getRoomParametersAreaText(areaEnabledState),
    });
    changeInputsParams(areaEnabledState);
  };

  const onChangeSurfaceClick = () => {
    setRoute('surface');
  };

  const onInputFocus = (fieldName: string) => () => {
    gaFocusOnDimensions({ fieldName: dimensionsLabels[fieldName], title: 'Параметры помещения' });
  };

  const roomParamsQuantity = getRoomParamsQuantity(
    filteredInputParamsWithoutFrontendConfigurationInputParams
  );

  useEffect(() => {
    setIsAreaEnabledHandler(filteredInputParams, setIsAreaEnabled);
  }, [filteredInputParams, setIsAreaEnabled]);

  useEffect(() => {
    setHasNegativeParams(checkIsSumWrong(formValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue]);

  useEffect(() => {
    setShowWarningBarHandler(hasNegativeParams, setShowWarningBar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNegativeParams]);

  return (
    <RoomParametersFormWrapper fixedArea={hasArea}>
      <RoomParametersFormControls>
        {hasArea && (
          <ButtonContainer>
            <TabList
              size="s"
              active={activeTabIndex}
              onChangeTab={event => {
                onAreaButtonClick((event.target as HTMLInputElement).value);
              }}
              className={styledTabList}>
              {tabListLabels.map((el, index) => (
                <Tab key={el.id} id={String(index)} value={index}>
                  {el.label}
                </Tab>
              ))}
            </TabList>
            <HeaderContainer>
              {hasArea && !mobile && (
                <Adaptive to="tablet">
                  <Button
                    data-testid="change-params-button"
                    variant="pseudo"
                    size="s"
                    label="Изменить параметры"
                    onClick={onChangeSurfaceClick}>
                    <Pencil />
                  </Button>
                </Adaptive>
              )}
            </HeaderContainer>
          </ButtonContainer>
        )}
        {isShowInfobar && (
          <InfoBar icon={<InfoIcon />} title="" type="secondary" className={infobar}>
            Необходимо указать параметры комнаты для расчёта всех поверхностей
          </InfoBar>
        )}

        {frontendConfigurationInputParams.size && frontendConfigurationInputs.FREE?.length ? (
          <FrontendConfigurationFields frontendConfiguration={frontendConfigurationInputs.FREE} />
        ) : null}
        {frontendConfigurationInputParams.size && frontendConfigurationInputs.RESTRICTED?.length ? (
          <FrontendConfigurationLayingTypes
            fieldName="typeInstallation"
            layingType={frontendConfigurationInputs.RESTRICTED}
            onChangeValue={handleUnselectRoom}
          />
        ) : null}

        {frontendConfigurationInputParams.size && frontendConfigurationInputs.CHECKBOX?.length ? (
          <FrontendConfigurationChackboxes checkboxes={frontendConfigurationInputs.CHECKBOX} />
        ) : null}

        {frontendConfigurationInputParams.size === 0 ? (
          <>
            {roomParamsQuantity.map((roomsParams, i) => (
              <InputsContainer last={i + 1 === roomParamsQuantity.length} key={roomsParams.key}>
                <InputContainerRow key={roomsParams.key}>
                  {roomsParams.inputParameters.map(parameter => (
                    <InputContainerColumn key={parameter}>
                      <Controller
                        name={parameter}
                        control={control}
                        rules={{
                          validate: {
                            required: value => roomParametersValidation(value, parameter),
                          },
                          pattern: {
                            value: dimensionsErrors[parameter]?.value,
                            message: dimensionsErrors[parameter]?.pattern,
                          },
                        }}
                        render={({ field: { value, onChange } }) => (
                          <>
                            <Adaptive to="tablet">
                              <AdaptiveInput
                                form={{
                                  value,
                                  onChange: handleChange(onChange),
                                  onFocus: onInputFocus(parameter),
                                  errors,
                                }}
                                name={parameter}
                                locale={{ labels: dimensionsLabels }}
                                mobile
                              />
                            </Adaptive>
                            <Adaptive from="tablet">
                              <AdaptiveInput
                                form={{
                                  value,
                                  onChange: handleChange(onChange),
                                  onFocus: onInputFocus(parameter),
                                  errors,
                                }}
                                name={parameter}
                                locale={{ labels: dimensionsLabels }}
                                mobile={false}
                              />
                            </Adaptive>
                          </>
                        )}
                      />
                    </InputContainerColumn>
                  ))}
                </InputContainerRow>
              </InputsContainer>
            ))}
            {hasThickness && (
              <LayingTypeContainer>
                <LayingTypeField fieldName="thickness" onChangeValue={handleUnselectRoom} />
              </LayingTypeContainer>
            )}
          </>
        ) : null}
      </RoomParametersFormControls>
    </RoomParametersFormWrapper>
  );
};
